import pattern from "@assets/images/about-us/patten.png";
import { NavigationTabs } from "./NavigationTabs";
import { useActiveTab, useTabContent } from "./hooks";
import { LeadershipSectionProps } from "./types";

/**
 * Leadership Section Component
 *
 * Features:
 * - Tab-based navigation using URL search parameters
 * - Dynamic content rendering based on active tab
 * - Responsive design with pattern background
 * - Automatic scroll prevention for better UX
 *
 * @param data - About us data containing member information
 */
export function LeadershipSection({ data }: LeadershipSectionProps) {
  const { activeTab } = useActiveTab();
  const { getContentComponent } = useTabContent();

  /**
   * Renders the appropriate content component based on active tab
   * Fallback to boardOfDirectors if tab is invalid
   */
  const renderContent = () => {
    const ContentComponent = getContentComponent(activeTab);
    return <ContentComponent data={data} />;
  };

  return (
    <section
      className="relative scroll-mt-10 bg-[#F7F9FC] pt-10 sm:pt-12 md:pt-16 lg:pt-20 3xl:pt-24"
      aria-label="Leadership and Management Section"
      id="leadership-section"
    >
      <img
        src={pattern}
        role="presentation"
        className="absolute end-0 top-[-200px] z-0 w-[300px] opacity-80 sm:top-[-300px] sm:w-[400px] md:top-[-360px] md:w-[500px] lg:top-[-460px] lg:w-auto lg:opacity-100 3xl:top-[-520px] 3xl:w-[700px] rtl:scale-x-[-1]"
      />

      <div className="relative">
        {/* Navigation Tabs */}
        <NavigationTabs activeTab={activeTab} />

        {/* Dynamic Content */}
        <div role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
          {renderContent()}
        </div>
      </div>
    </section>
  );
}
